import { getApiBaseUrl } from '../config/api.config'
import { PACKAGING_PRODUCT_CODE } from '../constants'
import { getTokenFromUrl } from '../utils/auth'
import { getCategoryImageSuffix } from '../utils/imageUrl'

import type { Environment } from '../App'
import type { LabelKey } from '../i18n'
import type {
  Order,
  SearchOrdersRequest,
  SearchOrdersResponse,
} from '../types/order'
import type { OrderDetailsResponse } from '../types/orderDetails'

export class OrdersApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: unknown,
  ) {
    super(message)
    this.name = 'OrdersApiError'
  }
}

export async function searchOrders(
  request: SearchOrdersRequest,
  env: Environment,
  authToken?: string,
  signal?: AbortSignal,
): Promise<SearchOrdersResponse> {
  const baseUrl = getApiBaseUrl(env)
  const url = `${baseUrl}/public/orderhistoryapi/internal/webstore/orders/search`

  const token = authToken || getTokenFromUrl()

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Add Authorization header if token is present
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
    console.log('[OrdersAPI] Authorization header added')
  } else {
    console.warn(
      '[OrdersAPI] No token found in URL. Add ?token=YOUR_TOKEN to the URL',
    )
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(request),
      signal,
    })

    console.log('[OrdersAPI] Response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new OrdersApiError(
        `Failed to search orders: ${response.statusText}`,
        response.status,
        errorText,
      )
    }

    const data = await response.json()

    return data as SearchOrdersResponse
  } catch (error) {
    if (error instanceof OrdersApiError) {
      throw error
    }

    // An abort is the caller withdrawing the request, not a failure. Wrapping it
    // would report "network error" for an ordinary unmount or account switch.
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error
    }

    throw new OrdersApiError(
      `Network error while searching orders: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }
}

/** Orders per request. */
export const ORDERS_PAGE_SIZE = 20

/**
 * How many pages have to be seen before we know whether the list continues.
 *
 * Two, not one: a short page is the only end-of-list signal this endpoint
 * gives, and page 0 alone reports "ended" only for accounts of 20 orders or
 * fewer — a 30-order account fills page 0 and proves nothing. Page 1 settles
 * it, so accounts up to 40 orders never cost more than two requests.
 */
export const ORDERS_FIRST_WAVE = 2

/**
 * Pages per background wave after the probe. Nothing renders off these, so the
 * only cost of a wider wave is requests we may not need; the only cost of a
 * narrower one is more sequential round trips for a long history.
 */
export const ORDERS_BACKGROUND_WAVE = 3

/**
 * Hard ceiling on pages, i.e. 100 orders.
 *
 * Two jobs. It stops a backend that never returns a short page from looping
 * forever, and it bounds the rendered list: there is no virtualisation here, so
 * every fetched order becomes a mounted `OrderCard`. An account past this many
 * orders gets silently truncated — fixing that properly needs paging in the UI,
 * not a bigger number.
 *
 * Deliberately back at 100 after a spell at 500. Loading five times the rows to
 * serve the handful of customers who have them cost every long-history account
 * mounted cards it never scrolled to, and the requests to fetch them. The list
 * still walks only as far as the history actually goes, so accounts under 100
 * are unaffected either way.
 */
export const ORDERS_MAX_PAGES = 5

/**
 * Newest first.
 *
 * An unparseable or missing `orderDate` sorts to the end instead of poisoning
 * the comparator: every comparison against `NaN` is false, which would leave
 * the surrounding run in whatever order the pages happened to resolve in.
 */
export function sortOrdersByDateDesc(orders: Order[]): Order[] {
  return [...orders].sort((a, b) => {
    const aTime = new Date(a.orderDate).getTime()
    const bTime = new Date(b.orderDate).getTime()
    const aValid = Number.isFinite(aTime)
    const bValid = Number.isFinite(bTime)

    if (!aValid && !bValid) return 0
    if (!aValid) return 1
    if (!bValid) return -1

    return bTime - aTime
  })
}

/**
 * Merge fetched pages into the list the UI renders: newest first, one row per
 * order.
 *
 * The dedupe is not defensive padding. The first page and the background pages
 * are fetched at different times, so an order placed in between shifts the
 * offset window and the same order comes back on both — left in, React sees two
 * children with the same `key={order.orderId}`. Earlier pages win, so the
 * newest rows keep the copy that was already on screen.
 */
export function mergeOrderPages(...pages: Order[][]): Order[] {
  const byId = new Map<string, Order>()

  for (const page of pages) {
    for (const order of page) {
      if (!byId.has(order.orderId)) {
        byId.set(order.orderId, order)
      }
    }
  }

  return sortOrdersByDateDesc([...byId.values()])
}

/**
 * Fetch the given pages in parallel.
 *
 * `allSettled` rather than `all` so every page is finished before we decide —
 * `all` rejects on the first failure and abandons the rest in flight.
 *
 * A failed page fails the whole wave rather than resolving with a hole in it:
 * a wave's pages are contiguous, so dropping one silently removes a run of
 * orders from the middle of the list.
 */
async function fetchOrderPages(
  pages: number[],
  request: Omit<SearchOrdersRequest, 'start' | 'limit'>,
  env: Environment,
  authToken?: string,
  signal?: AbortSignal,
): Promise<Order[][]> {
  const results = await Promise.allSettled(
    pages.map((page) =>
      searchOrders(
        {
          ...request,
          start: page * ORDERS_PAGE_SIZE,
          limit: ORDERS_PAGE_SIZE,
        },
        env,
        authToken,
        signal,
      ),
    ),
  )

  const rejected = results.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected',
  )
  if (rejected.length > 0) {
    throw rejected[0].reason
  }

  return results.map((result) =>
    result.status === 'fulfilled' ? result.value : [],
  )
}

/**
 * A page holding fewer than `ORDERS_PAGE_SIZE` rows is the last one, so every
 * page after it would come back empty.
 *
 * This is the only end-of-list signal the endpoint gives us — the response is a
 * bare array with no total and no `hasMore`. It assumes the API never returns a
 * short page that is not the final one; if it ever starts filtering rows out
 * *after* paging, this would stop early and hide orders.
 */
function reachedEndOfList(pages: Order[][]): boolean {
  return pages.some((page) => page.length < ORDERS_PAGE_SIZE)
}

/**
 * The first page — the one the list paints with.
 *
 * Deliberately a single request so time-to-first-order is one round trip of the
 * smallest query the endpoint serves. The single `limit: 100` call this
 * replaced was slow out of all proportion to its size.
 *
 * A result shorter than `ORDERS_PAGE_SIZE` is the whole list, and the caller
 * should skip `searchRemainingOrders` entirely.
 */
export async function searchFirstOrderPage(
  request: Omit<SearchOrdersRequest, 'start' | 'limit'>,
  env: Environment,
  authToken?: string,
  signal?: AbortSignal,
): Promise<SearchOrdersResponse> {
  const [firstPage] = await fetchOrderPages(
    [0],
    request,
    env,
    authToken,
    signal,
  )
  return firstPage
}

/**
 * Everything after the first page, for the caller to fetch in the background
 * and merge once it lands. Unsorted — the caller sorts the union, since neither
 * half is meaningful on its own.
 *
 * Keeps requesting waves until a short page ends the list or `ORDERS_MAX_PAGES`
 * stops it, so a history longer than one wave is not truncated. This costs a
 * long-history account more sequential round trips, but nothing here blocks a
 * render: the first page is already on screen throughout.
 *
 * The first wave is one page on purpose — with the foreground page that makes
 * `ORDERS_FIRST_WAVE`, enough to settle the common short-history case in two
 * requests before committing to a wider fan-out.
 *
 * `start` is a zero-based offset, so pages stay contiguous at `page * size`
 * (20, 40, 60, …) — no gap between the end of one page and the start of the
 * next.
 */
export async function searchRemainingOrders(
  request: Omit<SearchOrdersRequest, 'start' | 'limit'>,
  env: Environment,
  authToken?: string,
  signal?: AbortSignal,
): Promise<SearchOrdersResponse> {
  const collected: Order[][] = []
  let nextPage = 1
  let waveSize = ORDERS_FIRST_WAVE - 1

  while (nextPage < ORDERS_MAX_PAGES) {
    const pages = Array.from(
      { length: Math.min(waveSize, ORDERS_MAX_PAGES - nextPage) },
      (_, index) => nextPage + index,
    )

    // An empty wave cannot advance `nextPage`, so continuing would spin without
    // ever awaiting real work. Only reachable by tuning a wave constant to zero
    // or below, but the failure mode is a frozen tab rather than a bad list.
    if (pages.length === 0) break

    const wave = await fetchOrderPages(pages, request, env, authToken, signal)
    collected.push(...wave)

    if (reachedEndOfList(wave)) break

    nextPage += pages.length
    waveSize = ORDERS_BACKGROUND_WAVE
  }

  return collected.flat()
}

export { formatPrice } from '../utils/currency'

/**
 * Optimize image URL by adding size parameters if CDN supports it
 * Only adds transformations if URL doesn't already have them
 */
export function optimizeImageUrl(url: string, width: number = 180): string {
  if (!url) return url

  try {
    const transformations = `w_${width},c_fill,b_rgb:efefef,f_auto,q_auto`

    // Strip all existing transformation segments and replace them with ours.
    // Version segments start with 'v' followed by digits and are preserved.
    const stripped = url.replace(
      /\/image\/upload\/(?:[^/]+\/)+(?=v\d+\/)/,
      '/image/upload/',
    )

    return stripped.replace(
      '/image/upload/',
      `/image/upload/${transformations}/`,
    )
  } catch {
    return url
  }
}

/**
 * Order items excluding the free/premium packaging SKU, which isn't a
 * purchased product — it shouldn't count towards "multiple products", supply
 * the card thumbnail, or inflate the item count badge (RP-6061).
 */
function purchasedItems(order: Order): Order['items'] {
  return order.items.filter(
    (item) => item.productCode !== PACKAGING_PRODUCT_CODE,
  )
}

/**
 * Helper function to get the first product image URL
 * Selects the correct image based on product category mapping
 * Returns optimized URL for better performance
 */
export function getFirstProductImage(
  order: Order,
  width: number = 180,
): string | null {
  const resolveImageForItem = (item: Order['items'][number]): string | null => {
    if (!item.images || item.images.length === 0) return null

    // Use first image to determine the category
    const firstImage = item.images[0]
    const suffix = getCategoryImageSuffix(firstImage.fullPath)

    if (suffix) {
      // Find the image that matches the category suffix
      const matchedImage = item.images.find((img) =>
        img.fullPath?.endsWith(suffix),
      )

      if (matchedImage) {
        const imageUrl = matchedImage.secureUrl || matchedImage.fullPath
        return optimizeImageUrl(imageUrl, width)
      }
    }

    // Fallback to first image if no category match.
    // Use secureUrl — bare fullPath (e.g. COG campaign assets) is not a valid img src.
    if (!firstImage.fullPath) return firstImage.secureUrl || null
    return firstImage.secureUrl
      ? optimizeImageUrl(firstImage.secureUrl, width)
      : null
  }

  // If order contains custom-made item, always represent that item in the card.
  // Do not fall back to another item's image when custom-made has no image.
  const customMadeItem = order.items.find(
    (item) => item.customMadeConfigurationId !== null || item.isCustomMadeLite,
  )
  if (customMadeItem) {
    if (!customMadeItem.images || customMadeItem.images.length === 0) {
      return null
    }
    const firstImage = customMadeItem.images[0]

    // Keep custom-made representation deterministic: image always comes from the
    // custom-made item itself, without category-based remapping.
    if (!firstImage.fullPath) return firstImage.secureUrl
    return optimizeImageUrl(firstImage.fullPath, width)
  }

  for (const item of purchasedItems(order)) {
    const image = resolveImageForItem(item)
    if (image) return image
  }
  return null
}

/**
 * Helper function to check if order has custom made items that get the CM icon
 * on the order list card. Custom Made Lite items also carry a configuration id,
 * but they do not show the icon.
 */
export function hasCustomMadeItems(order: Order): boolean {
  return order.items.some(
    (item) => item.customMadeConfigurationId !== null && !item.isCustomMadeLite,
  )
}

/**
 * Helper function to check if order has multiple products
 */
export function hasMultipleProducts(order: Order): boolean {
  return purchasedItems(order).length > 1
}

/**
 * Item count for the order list card.
 *
 * `order.count` comes from the Order History API and includes the free/premium
 * packaging SKU, which isn't a purchased item and shouldn't inflate the badge
 * (RP-6061). Subtracted as a *line* count, not a summed quantity: packaging
 * lines always carry quantity 1 (RP-6019), so the two agree for packaging
 * today, and a line count stays correct whether `order.count` itself sums
 * quantities or counts lines — the API doesn't document which, so this
 * doesn't need to assume.
 *
 * Clamped at 0 in case `count` and `items` ever disagree about how much
 * packaging the order carries.
 */
export function getDisplayItemCount(order: Order): number {
  const packagingLineCount = order.items.filter(
    (item) => item.productCode === PACKAGING_PRODUCT_CODE,
  ).length

  return Math.max(0, order.count - packagingLineCount)
}

/**
 * Get the correct product image from order details item based on category
 */
export function getProductImageFromDetails(
  images: { fullPath: string; secureUrl: string }[] | undefined | null,
  width: number = 180,
  classCode?: string,
): string {
  if (!images || images.length === 0) return ''

  // COG (Customer Owned Good) campaign assets: return secureUrl as-is — their
  // fullPaths are non-Cloudinary campaign paths and should not be transformed.
  if (classCode === 'COG') {
    return images[0]?.secureUrl || ''
  }

  // eGift Cards: the order-details API may return a secureUrl with
  // pre-applied Cloudinary transformations (e.g. ar_10:20,b_rgb:...).
  // Strip any transformation segments before the version token so the
  // browser receives the clean versioned URL without any resize/crop/colour
  // params — Cloudinary serves the original file from that URL.
  if (classCode === 'EGIFTCARD') {
    const rawUrl = images[0]?.secureUrl
    if (!rawUrl) return ''
    return rawUrl.replace(
      /\/image\/upload\/(?:[^/]+\/)+(?=v\d+\/)/,
      '/image/upload/',
    )
  }

  const firstImage = images[0]
  const suffix = getCategoryImageSuffix(firstImage.fullPath)

  if (suffix) {
    const matchedImage = images.find((img) => img.fullPath?.endsWith(suffix))

    if (matchedImage?.secureUrl) {
      return optimizeImageUrl(matchedImage.secureUrl, width)
    }
  }

  // Fallback: use secureUrl directly when fullPath is null or no category/suffix
  // match — avoids applying broken Cloudinary transforms to non-standard URLs.
  return firstImage.secureUrl || ''
}

/**
 * Order status mapping based on fulfillment status ID
 */
const ORDER_STATUS_MAP: Array<[LabelKey, string[]]> = [
  ['orderList.status.orderConfirmed', ['1000']],
  ['orderList.status.inProgress', ['2000', '3600.2']],
  ['orderList.status.onItsWay', ['3000', '7000']],
  ['orderList.status.readyForPickup', ['3600']],
  ['orderList.status.delivered', ['7000', '7500']],
  ['orderList.status.purchased', ['7000']],
]

/**
 * Helper function to map fulfillment status ID to display status
 * Returns null if maxFulfillmentStatusId is null or no match is found
 */
export function getOrderDisplayStatus(
  maxFulfillmentStatusId: string | null,
): LabelKey | null {
  if (!maxFulfillmentStatusId) {
    return null
  }

  // Find the first matching status
  for (const [labelKey, statusIds] of ORDER_STATUS_MAP) {
    if (statusIds.includes(maxFulfillmentStatusId)) {
      return labelKey
    }
  }

  // Return null if no match found
  return null
}

/**
 * Fetch detailed information for a specific order
 */
export async function getOrderDetails(
  orderId: string,
  accountNumber: string,
  env: Environment,
  authToken?: string,
): Promise<OrderDetailsResponse> {
  const baseUrl = getApiBaseUrl(env)
  const url = `${baseUrl}/public/orderhistoryapi/internal/webstore/orders/${orderId}?accountNumber=${accountNumber}`

  // Get token from URL (temporary solution for testing)
  const token = authToken || getTokenFromUrl()

  console.log('[OrdersAPI] Get Order Details:', {
    url,
    orderId,
    accountNumber,
    env,
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 20)}...` : null,
  })

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Add Authorization header if token is present
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
    console.log('[OrdersAPI] Authorization header added')
  } else {
    console.warn(
      '[OrdersAPI] No token found in URL. Add ?token=YOUR_TOKEN to the URL',
    )
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'omit',
    })

    console.log('[OrdersAPI] Response status:', response.status)

    if (!response.ok) {
      const errorBody: unknown = await response.json().catch(() => null)
      const errorMessage =
        errorBody &&
        typeof errorBody === 'object' &&
        'message' in errorBody &&
        typeof (errorBody as { message: unknown }).message === 'string'
          ? (errorBody as { message: string }).message
          : response.statusText
      throw new OrdersApiError(
        `Failed to fetch order details: ${errorMessage}`,
        response.status,
        errorBody,
      )
    }

    const data = (await response.json()) as unknown as OrderDetailsResponse
    console.log('[OrdersAPI] Order details fetched:', {
      orderId: data.orderId,
    })
    return data
  } catch (error) {
    if (error instanceof OrdersApiError) {
      throw error
    }

    throw new OrdersApiError(
      `Network error while fetching order details: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }
}

/**
 * Cancel order lines for a specific order.
 */
export async function cancelOrderLines(
  orderId: string,
  orderLineIds: string[],
  accountNumber: string,
  env: Environment,
  authToken?: string,
): Promise<void> {
  const baseUrl = getApiBaseUrl(env)
  const cancelUrl = `${baseUrl}/public/saleorderapi/v1/orders/${orderId}/lines/cancel`
  const token = authToken || getTokenFromUrl()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  try {
    // Submit cancellation for selected lines
    const response = await fetch(cancelUrl, {
      method: 'POST',
      headers,
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify({
        orderLineIds,
        accountNumber,
        email: '',
      }),
    })

    if (!response.ok) {
      const errorBody: unknown = await response
        .json()
        .catch(() => response.text().catch(() => null))
      throw new OrdersApiError(
        `Failed to cancel order lines: ${response.statusText}`,
        response.status,
        errorBody,
      )
    }
  } catch (error) {
    if (error instanceof OrdersApiError) {
      throw error
    }

    throw new OrdersApiError(
      `Network error while cancelling order lines: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }
}
